/** @jsx jsx */

import { useState } from 'react';
import { jsx, css } from '@emotion/core';

import { Grid, Radio, RadioGroup, Surface, Text } from '@zapier/design-system';

import CtaAdminOnlyAccess from 'app/developer-v3/components/CtaAdminOnlyAccess';
import EditorBodyLayout from 'app/developer-v3/layouts/EditorBodyLayout';
import Link from 'app/common/components/Link';
import { AUTH_SCHEMES, BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import {
  CurrentVersionReadOnlyReason,
  useCurrentVersionReadOnlyReason,
} from 'app/developer-v3/hooks';

const Styles = {
  block: css`
    padding: 15px;
  `,
};

const authSchemeOrder = ['custom', 'oauth2', 'session', 'basic', 'digest'];
const authSchemesSorted = AUTH_SCHEMES.sort(
  (schemeX, schemeY) =>
    authSchemeOrder.findIndex(scheme => scheme === schemeX.value) -
    authSchemeOrder.findIndex(scheme => scheme === schemeY.value)
);

type Props = {
  isSaving: boolean;
  onSaveAuthenticationSchemeType: (string) => void;
};

type AuthScheme = 'custom' | 'oauth2' | 'session' | 'basic' | 'digest';

const AuthenticationSchemePicker = (props: Props) => {
  const [authType, setAuthType] = useState<AuthScheme>();

  const currentVersionReadOnlyReason = useCurrentVersionReadOnlyReason();
  const isReadOnly = !!currentVersionReadOnlyReason;
  const isNotAdmin =
    currentVersionReadOnlyReason === CurrentVersionReadOnlyReason.NOT_ADMIN;

  const onChangeScheme = (value: AuthScheme) => setAuthType(value);

  return (
    <EditorBodyLayout
      isDisabled={!authType || isReadOnly}
      isSaving={props.isSaving}
      onSave={() => props.onSaveAuthenticationSchemeType(authType)}
      title="Authentication"
    >
      <Grid columns={1} gap={15}>
        {isNotAdmin && <CtaAdminOnlyAccess />}
        <Surface borderColor="GrayWarm4">
          <section css={Styles.block}>
            <div>
              <Text fontWeight={600} margin="0px 0px 5px" tag="h3" type="Body4">
                Add Authentication
              </Text>
              <Text color="GrayWarm7">
                Authentication lets users prove their identity to your app and
                authorize Zapier to access their data, using your API
                authentication scheme.{' '}
                <Link href={`${BASE_DOC_URL_VB}/build/auth`} target="_blank">
                  Learn more
                </Link>
                .
              </Text>
            </div>
          </section>
        </Surface>
        <RadioGroup
          aria-label="Authentication scheme"
          isDisabled={isReadOnly}
          onChange={onChangeScheme}
          value={authType}
        >
          {authSchemesSorted.map(scheme => (
            <Surface
              borderColor={
                authType === scheme.value ? 'UiPrimaryStrongest' : 'GrayWarm4'
              }
              key={scheme.value}
            >
              <div css={Styles.block}>
                <Grid alignItems="center" gap={20}>
                  <Radio value={scheme.value}>{scheme.label}</Radio>
                </Grid>
                <Text margin="10px 0px 0px" tag="p">
                  {scheme.helpText}
                </Text>
              </div>
            </Surface>
          ))}
        </RadioGroup>
      </Grid>
    </EditorBodyLayout>
  );
};

export default AuthenticationSchemePicker;
